/**
 * In-app human support API (gap row 48, I8) — user-service.
 *
 *   requester side : /api/v1/users/support/*        (own threads only)
 *   platform team  : /api/v1/users/admin/support/*  (platform_superadmin)
 *
 * Attachments are optional (≤ 3 files of ≤ 2 MB: images, PDF, text/CSV) and
 * travel as base64 in the JSON body; downloads stream back through the bearer.
 */

import { getConfig } from '@/config';
import { getAccessToken } from '@/auth/oidc';
import { api } from '@/lib/api/client';

const BASE = '/api/v1/users/support';
const ADMIN = '/api/v1/users/admin/support';

export type SupportCategory = 'question' | 'bug' | 'billing' | 'feature' | 'account' | 'other';
export type SupportStatus = 'open' | 'pending' | 'resolved' | 'closed';
export type SupportPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface SupportAttachmentMeta {
  id: string;
  filename: string;
  content_type: string;
  size: number;
}

export interface SupportMessage {
  id: string;
  author_type: 'user' | 'staff' | 'system';
  author_name: string | null;
  body: string;
  created_at: string | null;
  attachments: SupportAttachmentMeta[];
  notify_status?: string | null;
}

export interface SupportThread {
  id: string;
  tenant_id: string;
  subject: string;
  category: SupportCategory;
  priority: SupportPriority;
  status: SupportStatus;
  user_id: string;
  user_email: string | null;
  user_name: string | null;
  page_url: string | null;
  assignee_id: string | null;
  assignee_email: string | null;
  last_message_at: string | null;
  last_message_preview: string | null;
  last_author_type: string | null;
  message_count: number;
  unread: number;
  created_at: string | null;
}

export interface SupportThreadDetail extends SupportThread {
  messages: SupportMessage[];
}

export interface AttachmentUpload {
  filename: string;
  content_type: string;
  data_base64: string;
}

export const SUPPORT_MAX_FILES = 3;
export const SUPPORT_MAX_BYTES = 2 * 1024 * 1024;
export const SUPPORT_ALLOWED_TYPES = [
  'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'application/pdf', 'text/plain', 'text/csv',
];

/** Validate + base64-encode picked files (throws a user-facing Error). */
export async function encodeAttachments(files: File[]): Promise<AttachmentUpload[]> {
  if (files.length > SUPPORT_MAX_FILES) throw new Error(`max_files:${SUPPORT_MAX_FILES}`);
  const out: AttachmentUpload[] = [];
  for (const f of files) {
    const type = (f.type || '').toLowerCase();
    if (!SUPPORT_ALLOWED_TYPES.includes(type)) throw new Error(`type:${f.name}`);
    if (f.size > SUPPORT_MAX_BYTES) throw new Error(`size:${f.name}`);
    const buf = new Uint8Array(await f.arrayBuffer());
    let bin = '';
    for (let i = 0; i < buf.length; i += 0x8000) {
      bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
    }
    out.push({ filename: f.name, content_type: type, data_base64: btoa(bin) });
  }
  return out;
}

export const supportApi = {
  list: () => api.get<{ items: SupportThread[]; total: number }>(`${BASE}/threads`),
  unread: () => api.get<{ unread: number; threads: number }>(`${BASE}/unread`),
  get: (id: string) => api.get<SupportThreadDetail>(`${BASE}/threads/${encodeURIComponent(id)}`),
  create: (body: {
    subject: string;
    category: SupportCategory;
    body: string;
    page_url?: string;
    locale?: string;
    attachments?: AttachmentUpload[];
  }) => api.post<SupportThreadDetail>(`${BASE}/threads`, body),
  reply: (id: string, body: string, attachments: AttachmentUpload[] = []) =>
    api.post<SupportThreadDetail>(`${BASE}/threads/${encodeURIComponent(id)}/messages`, { body, attachments }),
  resolve: (id: string) => api.post<SupportThreadDetail>(`${BASE}/threads/${encodeURIComponent(id)}/resolve`),
  remove: (id: string) => api.delete<void>(`${BASE}/threads/${encodeURIComponent(id)}`),
};

export interface SupportSummary {
  unread: number;
  open: number;
  pending: number;
  unassigned: number;
  mine: number;
}

export const supportAdminApi = {
  summary: () => api.get<SupportSummary>(`${ADMIN}/summary`),
  list: (params: { status?: SupportStatus | ''; assignee?: string; q?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.assignee) qs.set('assignee', params.assignee);
    if (params.q) qs.set('q', params.q);
    const s = qs.toString();
    return api.get<{ items: SupportThread[]; total: number }>(`${ADMIN}/threads${s ? `?${s}` : ''}`);
  },
  get: (id: string) => api.get<SupportThreadDetail>(`${ADMIN}/threads/${encodeURIComponent(id)}`),
  reply: (id: string, body: string, status?: SupportStatus, attachments: AttachmentUpload[] = []) =>
    api.post<SupportThreadDetail>(`${ADMIN}/threads/${encodeURIComponent(id)}/messages`, { body, status, attachments }),
  update: (id: string, body: { status?: SupportStatus; priority?: SupportPriority; assignee?: string; unassign?: boolean }) =>
    api.patch<SupportThreadDetail>(`${ADMIN}/threads/${encodeURIComponent(id)}`, body),
  remove: (id: string) => api.delete<void>(`${ADMIN}/threads/${encodeURIComponent(id)}`),
};

/** Download an attachment through the bearer (never a public URL). */
export async function downloadSupportAttachment(att: SupportAttachmentMeta, admin = false): Promise<void> {
  const token = await getAccessToken();
  const base = (getConfig().apiBaseUrl || '').replace(/\/$/, '');
  const path = `${admin ? ADMIN : BASE}/attachments/${encodeURIComponent(att.id)}`;
  const res = await fetch(`${base}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error(`download ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = att.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Window event other parts of the shell fire to open the support panel. */
export const OPEN_SUPPORT_EVENT = 'karexia:open-support';
export function openSupport(threadId?: string): void {
  window.dispatchEvent(new CustomEvent(OPEN_SUPPORT_EVENT, { detail: { threadId } }));
}
